import React, { useState } from "react";
import {
  ymca,
  codingNinjas,
  yamaha,
  concordia,
  awakinn,
  openText,
} from "../../assets/index";
import useWindowSize from "../../hooks/useWindowSize";
import ExperienceCard from "../ExperienceCard";
import Timeline from "@mui/lab/Timeline";
import TimelineItem, { timelineItemClasses } from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import ExperienceModal from "../ExperienceModal";

const experienceList = [
  {
    heading: "Software Engineer at OpenText",
    duration: "Oct 2025 - Present",
    logo: openText,
    description: `
     OpenText is a leading Canadian enterprise software company specializing in information management solutions that help organizations securely capture, manage, and analyze data. I work on the Core Data Discovery & Risk Insights team, which develops cloud-based tools for discovering, classifying, and managing sensitive data to reduce compliance and privacy risks across enterprise systems.
    `,
    sourceLink:
      "https://www.opentext.com/products/core-data-discovery-risk-insights",
    githubLink: "https://github.com/anshuman0799",
    linkedinLink: "https://www.linkedin.com/in/anshuman0799/",
  },

  {
    heading:
      "Software Engineering (M.Eng) student at Concordia University, Montreal, QC",
    duration: "Sep 2023 - May 2025",
    logo: concordia,
    description: `
     Graduated from M.Eng Software Engineering to enhance my skills and transition into a full-stack developer role. While my background has been predominantly in backend development, my recent focus on modern front-end technologies has sparked my interest in mastering both areas. This advanced degree is deepening my expertise and enabling me to manage complex, end-to-end software projects effectively. My commitment to this education underscores my passion for continuous learning and my goal to excel across all aspects of software engineering.
    `,
    linkedinLink: "https://www.linkedin.com/in/anshuman0799/",
    githubLink: "https://github.com/anshuman0799?tab=repositories",
  },
  {
    heading: "Software Engineer at Yamaha Motor Solutions India Pvt. Ltd.",
    duration: "Jul 2021 – Jul 2023",
    logo: yamaha,
    description: `
     At Yamaha Motor Solutions, I advanced to the role of Software Engineer and contributed to key projects, including working with SKY Network Television Limited in New Zealand. I helped develop and maintain integrations between their website, digital platform, and billing infrastructure. Additionally, I designed, tested, and deployed REST APIs and web applications to support the migration to a modern architecture. This role enhanced my skills in Java backend development and provided valuable experience with AWS, MongoDB, and Azure DevOps
    `,
    linkedinLink: "https://www.linkedin.com/in/anshuman0799/",
  },
  {
    heading:
      "Associate Software Engineer at Yamaha Motor Solutions India Pvt. Ltd.",
    duration: "Jan 2021 – Jul 2021",
    logo: yamaha,
    description: `
      In my role as an Associate Software Developer, I contributed to the development of a cross platform remote monitoring client application. This in-house enterprise tool provided actionable insights into employee productivity and operational efficiency, enhancing managerial oversight of workforce activities. During my tenure, I gained hands-on experience in enterprise software development, adhering to industry best practices, version control systems, and agile methodologies, while collaborating with cross-functional teams to ensure seamless deployment and functionality
    `,
    linkedinLink: "https://www.linkedin.com/in/anshuman0799/",
  },
  {
    heading: "DSA Problem Setter Intern at Coding Ninjas, Noida, India",
    duration: "Jul 2020 – Jan 2021",
    logo: codingNinjas,
    description: `
      At Coding Ninjas, I took on the role of a Problem Setter Intern, where I was entrusted with designing, documenting, and reviewing Data Structures and Algorithms (DSA) problems for the Code Studio website. This experience significantly sharpened my problem-solving abilities and deepened my understanding of complex algorithms.
    `,
    linkedinLink: "https://www.linkedin.com/in/anshuman0799/",
    sourceLink:
      "https://www.codingninjas.com/blog/2021/03/22/codestudio-a-coding-platform-to-prepare-practice/",
  },
  {
    heading: "Front-end Developer Intern at Awakinn: The coding hub, India",
    duration: "Nov 2018 – Feb 2019",
    logo: awakinn,
    description: `
     During my internship as a Front-End Developer, I was responsible for the design and development of the company's main website using HTML, CSS, and Bootstrap. Focusing solely on the front-end, I discovered a deep passion for crafting visually appealing and intuitive user interfaces. This experience was pivotal in igniting my interest in web development, highlighting the impact that thoughtful design and development can have on the overall user experience.
    `,
    linkedinLink: "https://www.linkedin.com/in/anshuman0799/",
    sourceLink: "https://proj-awakinn.vercel.app/",
  },
  {
    heading:
      "Bachelor of Technology, CSE from J.C. Bose University of Science & Technology, India",
    duration: "Jul 2017 - May 2021",
    logo: ymca,
    description: `
     I completed my Bachelor of Technology in Computer Science and Engineering, gaining a solid foundation in key areas like databases, networks, operating systems, and software engineering. During my final years, I worked on two significant projects: a COVID-19 Visualization and Prediction tool that utilized polynomial regression to forecast cases, deaths, and recoveries, and a Gestures to Text application that interpreted hand gestures using the Convex Hull algorithm. These experiences deepened my interest in data science and machine learning.
    `,
    linkedinLink: "https://www.linkedin.com/in/anshuman0799/",
    githubLink: "https://github.com/anshuman0799?tab=repositories",
  },
];

const Experience = () => {
  const { width } = useWindowSize();
  const isLargeScreen = width >= 768;
  const [selectedExperience, setSelectedExperience] = useState(null);

  const handleCardClick = (experience) => {
    setSelectedExperience(experience);
  };

  const handleCloseModal = () => {
    setSelectedExperience(null);
  };
  return (
    <section
      id="experience"
      className="w-full h-full py-10 flex flex-col border-b-[1px] border-b-black font-titleFont pb-20"
    >
      <h1 className="text-3xl lg:text-4xl font-normal w-full flex justify-center text-black mx-auto xl:pt-4">
        Experience
      </h1>
      <div className="xl:pl-40 xl:pr-40">
        <Timeline
          position={isLargeScreen ? "alternate" : undefined}
          sx={
            !isLargeScreen
              ? {
                  [`& .${timelineItemClasses.root}:before`]: {
                    flex: 0,
                    padding: 0,
                  },
                }
              : {}
          }
          className="mt-12"
        >
          {experienceList.map((experience) => (
            <TimelineItem key={experience.heading}>
              <TimelineSeparator>
                <TimelineDot sx={{ bgcolor: "#2257bf" }} />
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent>
                <ExperienceCard
                  heading={experience.heading}
                  duration={experience.duration}
                  logo={experience.logo}
                  onClick={() => handleCardClick(experience)}
                />
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      </div>

      <ExperienceModal
        isOpen={!!selectedExperience}
        onClose={handleCloseModal}
        experience={selectedExperience}
      />
    </section>
  );
};

export default Experience;
